import * as React from 'react';

import { Button, type ButtonProps } from '@/components/atoms/Button';
import { cn } from '@/components/atoms/primitives/utils';

/**
 * WEB-1001 — the global nav's borderless glass control (FRE-1363): gradient
 * fill + low-alpha inset ring instead of a 1px stroke, accent wash and glow on
 * hover / open / pressed. `circle` is an icon button (square, pill radius);
 * `pill` carries a label. Geometry comes from the button scale (32 / 40 / 48),
 * plus `nav`: the global nav's own 36 (WEB-1021, DS 1.9.0
 * `nav/loci-globalnav.css`). Every size keeps the Button's coarse-pointer
 * `::before` 44 hit floor.
 */
export type GlassControlSize = 'sm' | 'md' | 'lg' | 'nav';

export type GlassControlProps = Omit<
  Extract<ButtonProps, { toggle?: false; size?: 'sm' | 'md' | 'lg' }>,
  'variant' | 'icon' | 'round' | 'size'
> & {
  /** @default 'circle' */
  shape?: 'circle' | 'pill';
  /** @default 'md'. `nav` is the global nav's 36 — nav chrome only. */
  size?: GlassControlSize;
};

export const GlassControl = React.forwardRef<HTMLButtonElement, GlassControlProps>(
  function GlassControl({ shape = 'circle', size, className, ...props }, ref) {
    const isNav = size === 'nav';
    return (
      <Button
        ref={ref}
        icon={shape === 'circle'}
        round
        size={isNav ? 'md' : size}
        className={cn('a-glass-control', isNav && 'a-glass-control--nav', className)}
        {...props}
      />
    );
  },
);
